import styled, { css } from 'styled-components/macro';
import colors from 'styles/themes/colors';
import { stripUnit } from 'polished';
import { ThemeProps } from 'types/styled';
import { compose, layout } from 'styled-system';

export const Container = styled('section')<any>`
  padding: ${({ theme }): string => theme.spacing.large} 5%;
  background: white;

  @media screen and (max-width: 50em) {
    margin: 0 0.5em;
  }

  ${({ isMobileView }): string =>
    isMobileView
      ? `
      @media screen and (max-width: 50em) {
        margin: 0 0.5rem;
      }
      `
      : ''}

  @media screen and (min-width: 50em) {
    width: ${({ theme }): number => theme.layout.smallScreen * 100}%;
    padding: ${({ theme }): string =>
      `${theme.spacing.large} 2rem ${theme.spacing.large} ${theme.spacing.enormous}`};
    margin: 0 auto;
  }

  @media screen and (min-width: 80em) {
    width: ${({ theme }): number => theme.layout.largeScreen * 100}%;
  }
`;

export const KPIListItem = styled.li<ThemeProps<'li'>>`
  padding-left: ${({ theme }): string => theme.spacing.xsmall};
  padding-right: ${({ theme }): string => theme.spacing.xsmall};
  min-width: 8em;
  margin-top: 1em;

  @media screen and (min-width: 50em) {
    /** 1.95em is 1.2 line-height + 0.75em margin-bottom that a header would have. */
    padding-top: ${({ hasNoHeader }): string | number =>
      hasNoHeader ? '1.95em' : 0};
    padding-left: 0;
    padding-right: 0;
    ${({ doubleWidth }): any => doubleWidth && doubleWidthGridItem};

    ${({ verticalDivider, theme }): any =>
      verticalDivider &&
      css`
        @supports (grid-template-columns: repeat(auto-fit, minmax(8em, 1fr))) {
          position: relative;
          :after {
            display: block;
            content: '';
            position: absolute;
            right: -${Number(stripUnit(theme.spacing.xlarge)) / 2}em;
            top: 0;
            bottom: 0;
            width: 1px;
            background: ${colors.LightGrey};
          }
        }
      `};
  }

  @supports (grid-template-columns: repeat(auto-fit, minmax(8em, 1fr))) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const doubleWidthGridItem = css`
  flex: 2 0 auto;
  grid-column: span 2;
`;

export const KPIList = styled.ul<ThemeProps<'ul'>>`
  display: flex;
  flex-flow: row wrap;
  margin-left: -${({ theme }): string => theme.spacing.xsmall};
  margin-right: -${({ theme }): string => theme.spacing.xsmall};
  ${({ marginTop }): string => (marginTop ? `margin-top: ${marginTop};` : '')}
  ${({ marginBottom }): string =>
    marginBottom ? `margin-bottom: ${marginBottom};` : ''}

  ${KPIListItem} {
    ${({ columns }): any =>
      columns
        ? css`
            flex: 1 0 ${100 / columns.length}%;
          `
        : css`
            flex: 1 0 100%;

            @media screen and (min-width: 50em) {
              flex: 1 0 auto;
            }
          `};
  }

  @supports (grid-template-columns: repeat(auto-fit, minmax(8em, 1fr))) {
    display: grid;
    grid-template-columns: ${({ columns }): string =>
      columns ? columns.join(' ') : '1fr'};
    grid-gap: ${({ theme }): string =>
      `${theme.spacing.xsmall} ${theme.spacing.xlarge}`};
    margin-left: 0;
    margin-right: 0;

    @media screen and (min-width: 50em) {
      grid-template-columns: ${({ columns }): string =>
        columns ? columns.join(' ') : 'repeat(auto-fit, minmax(8em, 1fr))'};
    }
    @media screen and (min-width: 1439px) {
      grid-gap: ${({ theme }): string => `${theme.spacing.xsmall} 4em`};
    }
  }
`;

export const Header = styled('h3')<any>`
  ${compose(layout)};
  font-size: 1em;
  ${({ marginBottom }): string =>
    marginBottom ? `margin-bottom: ${marginBottom} !important;` : ''}
  ${({ marginTop }): string =>
    marginTop ? `margin-top: ${marginTop} !important;` : ''}
  font-weight: ${({ titleAlignLeft }): string =>
    titleAlignLeft ? 'bold' : '600'};
`;

export const KPIListWrapper = styled('div')<any>`
  margin: ${({ theme, isMobileView }): string =>
    `${isMobileView ? '0' : theme.spacing.xlarge} 0`};
`;

export const KPIListItemContent = styled('div')`
  display: flex;
  flex-flow: row wrap;
  margin: ${({ theme }): string => `-${theme.spacing.medium}`};
`;

export const KPIListItemContentItem = styled('div')<any>`
  display: flex;
  flex-flow: row wrap;
  flex: 1 1 ${({ itemsCount }): number => (itemsCount ? 100 / itemsCount : 50)}%;

  @media screen and (max-width: 40em) {
    flex: 1 1 80%;
  }

  @media screen and (max-width: 100em) {
    padding-top: ${({ theme }): string => theme.spacing.medium};
  }
`;

export const KPIListItemContentItemColumn = styled(KPIListItemContentItem)<any>`
  min-width: 12rem;
  flex-basis: auto;
  ${({ marginTop }): string => (marginTop ? `margin-top: ${marginTop};` : '')}
`;

export const KPIListItemRow = styled('div')`
  display: flex;
  flex-flow: row wrap;
  min-width: 100%;
  padding-top: ${({ theme }): string => theme.spacing.large};

  &:first-of-type {
    padding-top: 0;
  }
`;

export const KPIListTotal = styled('ul')`
  display: flex;
  flex-flow: row wrap;
  padding: 0;
  margin: ${({ theme }): string => `-${theme.spacing.medium}`};
  list-style-type: none;
`;

export const KPIItem = styled.li`
  flex-grow: 1;
  margin: ${({ theme }): string => theme.spacing.xlarge};
`;

export const KPIListItemColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }): string => `${theme.spacing.large}`};
`;

export const TWTDText = styled('span')`
  &:hover {
    cursor: pointer;
  }
`;

export const ListItemWrapper = styled('div')``;

export const DateRange = styled('div')<any>`
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 500;
  color: #8da5bf;
  margin: 4px 0 3px 0;
  ${({ marginLeft }): string =>
    marginLeft
      ? `
      margin-left: ${marginLeft};
    `
      : ''}
`;
